import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import LockClosedIcon from '@heroicons/react/outline/LockClosedIcon';
import { hasScopesPermission } from '@tribeplatform/gql-client/lib';
import { PinnedInto, PostListOrderByEnum, } from '@tribeplatform/gql-client/types';
import { usePinnedPosts, usePosts, useSpace, } from '@tribeplatform/react-sdk/hooks';
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils';
import { Card } from '@tribeplatform/react-ui-kit/Card';
import { EmptyState } from '@tribeplatform/react-ui-kit/EmptyState';
import { useI18n } from '../i18n';
import { getUserSettings, setUserSetting } from '../utils/userSettings';
import { EmptyState as PostsEmptyState } from './EmptyState';
import { GenericPostList } from './GenericPostList';
import { PostListFilter } from './PostListFilter';
export const SpacePostList = ({ spaceId, tagId, tagSlug, view, orderBy: defaultOrderBy = PostListOrderByEnum.publishedAt, }) => {
    var _a, _b, _c, _d;
    const intl = useI18n();
    const userSettingOrderBy = (_a = getUserSettings().postListOrderBy) === null || _a === void 0 ? void 0 : _a[spaceId];
    const [orderBy, setOrderBy] = useState(userSettingOrderBy !== null && userSettingOrderBy !== void 0 ? userSettingOrderBy : defaultOrderBy);
    useEffect(() => {
        setUserSetting('postListOrderBy', {
            [spaceId]: orderBy,
        });
    }, [spaceId, orderBy]);
    const { data: space } = useSpace({
        variables: { id: spaceId },
    });
    const [canGetPosts] = hasScopesPermission(space, ['posts']);
    const shouldFilterByTag = (tagId && tagId.length > 0) || (tagSlug && tagSlug.length > 0);
    let variables;
    if (shouldFilterByTag) {
        // TODO: This is a temporary solution until tags become filterable by posts query
        variables = {
            spaceId,
            tagId,
            tagSlug,
            limit: 5,
            orderBy,
        };
    }
    else {
        variables = {
            limit: 5,
            spaceIds: [spaceId],
            excludePins: true,
            orderBy,
        };
    }
    const { data, fetchNextPage = false, hasNextPage, isLoading, isFetched, isFetchingNextPage, } = usePosts({
        variables,
        fields: {
            tags: 'basic',
            owner: {
                member: { profilePicture: 'basic', badges: 'all' },
            },
            embeds: 'basic',
            mentions: 'basic',
            space: {
                authMemberProps: 'all',
                image: 'basic',
            },
            attachments: 'basic',
            authMemberProps: 'all',
            reactions: { fields: 'all', variables: { limit: 10 } },
            replies: {
                fields: {
                    authMemberProps: 'all',
                    embeds: 'basic',
                    mentions: 'basic',
                    attachments: 'basic',
                    owner: {
                        member: { profilePicture: 'basic', badges: 'all' },
                    },
                    reactions: { fields: 'all', variables: { limit: 10 } },
                },
                variables: {
                    limit: 2,
                    reverse: true,
                },
            },
        },
        useInfiniteQueryOptions: {
            refetchOnMount: 'always',
            enabled: canGetPosts,
        },
    });
    const pinnedPostsData = usePinnedPosts({
        variables: { spaceId },
        useQueryOptions: {
            enabled: canGetPosts,
        },
    });
    let { data: pinnedPosts } = pinnedPostsData;
    const { isLoading: isPinnedPostLoading } = pinnedPostsData;
    if (!canGetPosts && ((_b = space === null || space === void 0 ? void 0 : space.authMemberProps) === null || _b === void 0 ? void 0 : _b.permissions)) {
        return (_jsx(Card, { children: _jsx(Card.Content, { children: _jsx("div", { className: "my-16", children: _jsx(EmptyState, { title: intl.$t({
                            id: 'Alerts.PrivateSpace',
                            defaultMessage: 'This {SPACE} is private',
                        }), icon: _jsx(LockClosedIcon, {}, void 0), description: intl.$t({
                            id: 'Alerts.PrivateSpace.Description',
                            defaultMessage: 'You need to be invited to this {SPACE} to access its posts',
                        }) }, void 0) }, void 0) }, void 0) }, void 0));
    }
    let { nodes: posts } = simplifyPaginatedResult(data);
    if (shouldFilterByTag) {
        pinnedPosts = pinnedPosts === null || pinnedPosts === void 0 ? void 0 : pinnedPosts.filter(post => { var _a; return (_a = post.tags) === null || _a === void 0 ? void 0 : _a.some(tag => tag.id === tagId || tag.slug === tagSlug); });
        posts = posts.filter(post => { var _a; return !((_a = post.pinnedInto) === null || _a === void 0 ? void 0 : _a.includes(PinnedInto.space)); });
    }
    if (((_c = space === null || space === void 0 ? void 0 : space.authMemberProps) === null || _c === void 0 ? void 0 : _c.permissions) &&
        !isLoading &&
        !isPinnedPostLoading &&
        !(posts === null || posts === void 0 ? void 0 : posts.length) &&
        !(pinnedPosts === null || pinnedPosts === void 0 ? void 0 : pinnedPosts.length)) {
        return (_jsx(_Fragment, { children: _jsx(PostsEmptyState, {}, void 0) }, void 0));
    }
    return (_jsxs("div", { children: [_jsx(PostListFilter, { orderBy: orderBy, setOrderBy: setOrderBy }, void 0), _jsx(GenericPostList, { pinnedPosts: pinnedPosts, posts: posts, context: "space", activeTagId: tagId, activeTagSlug: tagSlug, fetchNextPage: fetchNextPage, hasNextPage: hasNextPage, isLoading: isLoading ||
                    isPinnedPostLoading ||
                    !((_d = space === null || space === void 0 ? void 0 : space.authMemberProps) === null || _d === void 0 ? void 0 : _d.permissions), view: view, isFetchingNextPage: isFetchingNextPage, isFetched: isFetched }, void 0)] }, void 0));
};
//# sourceMappingURL=SpacePostList.js.map